import {
  AdminWordService,
  AdminWordServiceOptions,
  WordOperationResult,
  ChangeAction,
} from "../AdminWordService";
import { WordExportType, WordTypeWithImages } from "../../features/words/types";
import { supabase } from "./index";
import { auth } from "../firebase";
import { WordImage } from "../WordService";

export class SupabaseAdminWordService extends AdminWordService {
  async addWord(word: WordTypeWithImages): Promise<WordOperationResult> {
    try {
      // First check if user is authenticated via Firebase
      if (!auth.currentUser) {
        return {
          success: false,
          error: `Error adding ${word.word}: User not authenticated`,
        };
      }

      // Extract images array and prepare word data without the legacy images column
      const { images, ...wordWithoutImages } = word;

      // Insert the word without the images field
      const { data, error } = await supabase
        .from("words_fr")
        .insert([wordWithoutImages])
        .select();

      if (error) {
        console.error("Supabase error:", error);
        return {
          success: false,
          error: `Error adding ${word.word}: ${error.message}`,
        };
      }

      if (!data || data.length === 0) {
        return {
          success: false,
          error: `Error adding ${word.word}: No data returned from insert`,
        };
      }

      // Handle word_image relationships if there are images
      if (images && images.length > 0) {
        await this.updateWordImages(word.word, images);
      }

      return {
        success: true,
        data: { ...data[0], images } as WordTypeWithImages,
      };
    } catch (error) {
      console.error("Error adding word:", error);
      return {
        success: false,
        error: `Error adding ${word.word}: ${error}`,
      };
    }
  }

  async getWord(word: string): Promise<WordOperationResult> {
    const { data, error } = await supabase
      .from("words_fr")
      .select()
      .eq("word", word)
      .maybeSingle();

    if (error) {
      return {
        success: false,
        error: `Error fetching ${word}: ${error.message}`,
      };
    }

    // Fetch images from word_image relationship table
    const images = await this.getWordImages(word);

    return {
      success: true,
      data: { ...data, images } as WordTypeWithImages,
    };
  }

  async updateWord(word: WordTypeWithImages): Promise<WordOperationResult> {
    try {
      // First check if user is authenticated via Firebase
      if (!auth.currentUser) {
        return {
          success: false,
          error: `Error updating ${word.word}: User not authenticated`,
        };
      }

      // Check if the word exists first (without RLS affecting this check)
      const { data: existingWord, error: fetchError } = await supabase
        .from("words_fr")
        .select("word")
        .eq("word", word.word)
        .maybeSingle();

      if (fetchError) {
        console.error("Error checking existing word:", fetchError);
        return {
          success: false,
          error: `Error updating ${word.word}: ${fetchError.message}`,
        };
      }

      if (!existingWord) {
        console.log("Word not found in database:", word.word);
        return {
          success: false,
          error: `Error updating ${word.word}: Word not found`,
        };
      }

      console.log("Word exists, proceeding with update");

      // Extract images array and prepare word data without the legacy images column
      const { images, ...wordWithoutImages } = word;

      const { data, error, count } = await supabase
        .from("words_fr")
        .update(wordWithoutImages)
        .eq("word", word.word)
        .select();

      console.log("Update result:", {
        data,
        error,
        count,
        dataLength: data?.length,
      });

      if (error) {
        console.error("Supabase update error:", error);
        return {
          success: false,
          error: `Error updating ${word.word}: ${error.message}`,
        };
      }

      if (!data || data.length === 0) {
        console.error("No rows updated - possible RLS policy issue");
        return {
          success: false,
          error: `Error updating ${word.word}: No rows were updated. This might be due to RLS policies blocking the update operation.`,
        };
      }

      // Handle word_image relationships only if images changed
      const currentImages = await this.getWordImages(word.word);
      const imagesChanged = this.hasImagesChanged(currentImages, images || []);

      if (imagesChanged && images) {
        await this.updateWordImages(word.word, images);
      }

      return {
        success: true,
        data: {
          ...data[0],
          images: images || currentImages,
        } as WordTypeWithImages,
      };
    } catch (error) {
      console.error("Error updating word:", error);
      return {
        success: false,
        error: `Error updating ${word.word}: ${error}`,
      };
    }
  }

  async deleteWord(word: string): Promise<boolean> {
    try {
      // First check if user is authenticated via Firebase
      if (!auth.currentUser) {
        console.error("Error deleting word: User not authenticated");
        return false;
      }

      // Check if the word exists first
      const { data: existingWord, error: fetchError } = await supabase
        .from("words_fr")
        .select("word")
        .eq("word", word)
        .maybeSingle();

      if (fetchError) {
        console.error("Error checking existing word:", fetchError);
        return false;
      }

      if (!existingWord) {
        console.error(`Error deleting ${word}: Word not found`);
        return false;
      }

      const { error } = await supabase
        .from("words_fr")
        .delete()
        .eq("word", word);

      if (error) {
        console.error("Error deleting word:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error deleting word:", error);
      return false;
    }
  }

  addChange(change: ChangeAction): Promise<void> {
    throw new Error("Method not implemented.");
  }

  async renameWord(
    oldWord: string,
    newWord: WordTypeWithImages
  ): Promise<WordOperationResult> {
    try {
      // In Supabase, we can use a transaction-like approach
      // First add the new word
      const addResult = await this.addWord(newWord);
      if (!addResult.success) {
        return addResult;
      }

      // Then delete the old word
      const deleted = await this.deleteWord(oldWord);
      if (!deleted) {
        // If delete fails, we might want to rollback the add
        // For now, we'll just return an error
        return {
          success: false,
          error: `Added new word but failed to delete old word: ${oldWord}`,
        };
      }

      return addResult;
    } catch (error) {
      console.error("Error renaming word:", error);
      return {
        success: false,
        error: `Error renaming ${oldWord} to ${newWord.word}: ${error}`,
      };
    }
  }

  async exportWords(
    options?: AdminWordServiceOptions
  ): Promise<WordExportType[]> {
    // The exportWords method is not implemented for SupabaseAdminWordService
    throw new Error("Method not implemented.");
  }

  // Get change history for a specific word
  async getWordHistory(word: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from("word_changes")
        .select("*")
        .eq("record_id", word)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error("Error fetching word history:", error);
      return [];
    }
  }

  // Get recent changes by user
  async getRecentChanges(limit: number = 50): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from("word_changes")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error("Error fetching recent changes:", error);
      return [];
    }
  }

  async searchImages(
    keyword: string,
    limit: number = 10
  ): Promise<WordImage[]> {
    try {
      const { data, error } = await supabase
        .from("images")
        .select("id, keyword, url")
        .ilike("keyword", `%${keyword}%`)
        .limit(limit);

      if (error) {
        throw new Error(error.message);
      }

      return data.map((item) => ({
        id: item.id,
        keyword: item.keyword,
        url: item.url,
      }));
    } catch (error) {
      console.error("Error searching images:", error);
      return [];
    }
  }

  private async getWordImages(word: string): Promise<WordImage[]> {
    try {
      const { data, error } = await supabase
        .from("word_image")
        .select("image")
        .eq("word", word);

      if (error) {
        console.error("Error fetching word images:", error);
        return [];
      }

      // Return full image URLs
      return data.map((item) => ({
        id: item.image,
        url: `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/images/${item.image}.png`,
      }));
    } catch (error) {
      console.error("Error fetching word images:", error);
      return [];
    }
  }

  /**
   * Check if images have changed by comparing image IDs
   */
  private hasImagesChanged(
    currentImages: WordImage[],
    newImages: WordImage[]
  ): boolean {
    if (currentImages.length !== newImages.length) {
      return true;
    }

    const currentIds = currentImages.map((img) => img.id).sort();
    const newIds = newImages.map((img) => img.id).sort();

    return currentIds.some((id, index) => id !== newIds[index]);
  }

  private async updateWordImages(
    word: string,
    images: WordImage[]
  ): Promise<void> {
    try {
      // first, delete existing relationships
      await supabase.from("word_image").delete().eq("word", word);

      const imageIds = images?.map((img) => img.id).filter((id) => id !== null);
      if (imageIds.length > 0) {
        const relationships = imageIds.map((imageId) => ({
          word: word,
          image: imageId,
        }));

        const { error } = await supabase
          .from("word_image")
          .insert(relationships);

        if (error) {
          console.error("Error updating word_image relationships:", error);
        }
      }
    } catch (error) {
      console.error("Error updating word images:", error);
    }
  }
}
